/**
 * Return the text labels for the active filters in the conversations page.
 */

import { StringFormat, formatString } from 'src/utils/format-string';
import { type IOtherUser, type IUser } from 'src/api/users-api';

export const getAssigneesFilterLabel = (
    assigneeIds: string[] | undefined,
    teamMembers: (IOtherUser | IUser)[] | undefined,
    prefix: string = 'Assignee is'
): string | undefined => {
    if (assigneeIds === undefined || !assigneeIds.length) {
        return undefined;
    }
    if (teamMembers === undefined) {
        return undefined;
    }

    return `${prefix} ${assigneeIds
        .map(assigneeId => `"${teamMembers?.find(member => member.id === Number(assigneeId))?.name || ''}"`)
        .filter(Boolean)
        .join(' or ')}`;
};

export const getIngestionSourcesFilterLabel = (
    ingestionSources: string[] | undefined
): string | undefined => {
    if (ingestionSources === undefined || !ingestionSources.length) {
        return undefined;
    }
    return `Ingestion source is ${ingestionSources
        .map(source => `"${source}"`)
        .join(' or ')}`;
};

export const getLeadStagesFilterLabel = (
    leadStages: string[] | undefined
): string | undefined => {
    if (leadStages === undefined || !leadStages.length) {
        return undefined;
    }
    return `Lead stage is ${leadStages
        .map(stage => `"${formatString(stage, StringFormat.SnakeCase, StringFormat.SentenceCase)}"`)
        .join(' or ')}`;
};

/**
 * If a lead label does not exist, it is skipped in the label.
 */
export const getLeadLabelsFilterLabel = (
    leadLabelIds: string[] | undefined, leadLabels: { id: number; name: string }[] | undefined
): string | undefined => {
    if (leadLabelIds === undefined || !leadLabelIds.length) {
        return undefined;
    }
    if (leadLabels === undefined) {
        return undefined;
    }
    const validLeadLabelIds = new Set(leadLabels.map(label => String(label.id)));
    leadLabelIds = leadLabelIds.filter(labelId => validLeadLabelIds.has(labelId));

    // check again after filtering
    if (leadLabelIds.length === 0) {
        return undefined;
    }

    return `Lead label is ${leadLabelIds
        .map(labelId => `"${leadLabels?.find(label => label.id === Number(labelId))?.name || ''}"`)
        .filter(Boolean)
        .join(' or ')}`;
};

export const getExcludeLeadLabelsFilterLabel = (
    excludeLeadLabelIds: string[] | undefined, leadLabels: { id: number; name: string }[] | undefined
): string | undefined => {
    if (excludeLeadLabelIds === undefined || !excludeLeadLabelIds.length) {
        return undefined;
    }
    if (leadLabels === undefined) {
        return undefined;
    }

    const validLeadLabelIds = new Set(leadLabels.map(label => String(label.id)));
    excludeLeadLabelIds = excludeLeadLabelIds.filter(labelId => validLeadLabelIds.has(labelId));

    // check again after filtering
    if (excludeLeadLabelIds.length === 0) {
        return undefined;
    }

    return `Lead label is not ${excludeLeadLabelIds
        .map(labelId => `"${leadLabels?.find(label => label.id === Number(labelId))?.name || ''}"`)
        .filter(Boolean)
        .join(' and ')}`;
};

/**
 * If a property does not exist, it is skipped in the label.
 */
export const getPropertyFilterLabel = (
    propertyIds: string[] | undefined, properties: { id: number; name: string }[] | undefined
): string | undefined => {
    if (propertyIds === undefined || !propertyIds.length) {
        return undefined;
    }
    if (properties === undefined) {
        return undefined;
    }

    const validPropertyIds = new Set(properties.map(property => String(property.id)));
    propertyIds = propertyIds.filter(propertyId => validPropertyIds.has(propertyId));

    // check again after filtering
    if (propertyIds.length === 0) {
        return undefined;
    }

    return `Property is ${(propertyIds ?? [])
        .map(propertyId => `"${properties?.find(property => property.id === Number(propertyId))?.name || ''}"`)
        .filter(Boolean)
        .join(' or ')}`;
};

/**
 * If a property label does not exist, it is skipped in the label.
 */
export const getPropertyLabelsFilterLabel = (
    propertyLabelIds: string[] | undefined, propertyLabels: { id: number; name: string }[] | undefined
): string | undefined => {
    if (propertyLabelIds === undefined || !propertyLabelIds.length) {
        return undefined;
    }
    if (propertyLabels === undefined) {
        return undefined;
    }

    const validPropertyLabelIds = new Set(propertyLabels.map(label => String(label.id)));
    propertyLabelIds = propertyLabelIds.filter(labelId => validPropertyLabelIds.has(labelId));

    // check again after filtering
    if (propertyLabelIds.length === 0) {
        return undefined;
    }

    return `Property label is ${propertyLabelIds
        .map(labelId => `"${propertyLabels?.find(label => label.id === Number(labelId))?.name || ''}"`)
        .filter(Boolean)
        .join(' or ')}`;
};

export const getArchivalStatusFilterLabel = (
    leadIsArchived: boolean | undefined
): string | undefined => {
    if (leadIsArchived === undefined) {
        return undefined;
    }
    return `Lead is ${leadIsArchived ? 'archived' : 'unarchived'}`;
};